import React, { useEffect, useState } from 'react';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import { FaTrash } from '@react-icons/all-files/fa/FaTrash';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import { FaPencilAlt } from '@react-icons/all-files/fa/FaPencilAlt';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntities } from '../../../../manager/categoria-actividad.reducer';
import UsuariosFilter from '../../../../commons/UsuariosFilter';
import ShieldIcon from '../../../../commons/ShieldIcon';

// ── Types ────────────────────────────────────────────────────────────────────

type NodeTipo = 'inicio' | 'actividad' | 'fin';

interface NodePropsPanel {
    selectedNode: any;
    updateNode: (node: any) => void;
    removeNode: (node: any) => void;
    addNodeFromSelected: (id: string) => void;
    permitirEliminarNodo?: boolean;
    permitirSeleccionarUsuario?: boolean;
    mostrarConfigActividad?: boolean;
    permitirAgregarHijo?: boolean;
    editarTipoNodo?: boolean;
    editarActividad?: ((node: any) => void) | boolean;
    children?: React.ReactNode;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function WorkflowDesignerProperties({
    selectedNode,
    updateNode,
    removeNode,
    addNodeFromSelected,
    permitirEliminarNodo = false,
    permitirSeleccionarUsuario = false,
    mostrarConfigActividad = true,
    permitirAgregarHijo = false,
    editarTipoNodo = false,
    editarActividad,
    children,
}: NodePropsPanel) {
    const dispatch = useAppDispatch();

    const categorias = useAppSelector(s => s.categoriaActividad.entities) as any[];

    const tipo: NodeTipo = selectedNode?.data?.tipo ?? selectedNode?.tipo ?? 'actividad';

    const [label, setLabel] = useState<string>(selectedNode?.data?.label ?? '');
    const [habilitado, setHabilitado] = useState<boolean>(selectedNode?.data?.habilitado ?? true);
    const [requiereAutorizacion, setRequiereAutorizacion] = useState<boolean>(selectedNode?.data?.requiereAutorizacion ?? false);
    const [tipoEdit, setTipoEdit] = useState<NodeTipo>(tipo);
    const [categoriaId, setCategoriaId] = useState<string>(String(selectedNode?.data?.categoriaActividad?.id ?? ''));
    const [usuario, setUsuario] = useState<any>(selectedNode?.data?.usuario ?? null);
    const [autoriza, setAutoriza] = useState<any>(selectedNode?.data?.autoriza ?? null);

    // Load categories once
    useEffect(() => {
        dispatch(getEntities({ page: 0, size: 99999, sort: 'descripcion,asc', filters: [] }));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync local state when selected node changes
    useEffect(() => {
        setLabel(selectedNode?.data?.label ?? '');
        setHabilitado(selectedNode?.data?.habilitado ?? true);
        setRequiereAutorizacion(selectedNode?.data?.requiereAutorizacion ?? false);
        setTipoEdit(selectedNode?.data?.tipo ?? selectedNode?.tipo ?? 'actividad');
        setCategoriaId(String(selectedNode?.data?.actividad?.id ?? ''));
        setUsuario(selectedNode?.data?.usuario ?? null);
        setAutoriza(selectedNode?.data?.autoriza ?? null);
    }, [selectedNode?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleApply = () => {
        const selectedCategoria = categorias.find(c => String(c.id) === categoriaId) ?? null;
        const descripcion = tipoEdit === 'actividad' ? selectedCategoria?.descripcion ?? '-- Sin Categoría' : (tipoEdit === 'inicio' ? 'Inicio' : (tipoEdit === 'fin' ? 'Fin' : 'desc...'));
        updateNode({
            ...selectedNode,
            tipo: tipoEdit,
            type: tipoEdit,
            habilitado,
            requiereAutorizacion,
            actividad: selectedCategoria,
            data: {
                ...selectedNode.data,
                label: descripcion,
                habilitado,
                requiereAutorizacion,
                tipo: tipoEdit,
                actividad: selectedCategoria,
                usuario,
                autoriza,
            },
        });
    };

    const handleEditarActividad = () => {
        if (typeof editarActividad === 'function') editarActividad(selectedNode);
    };

    const canDelete = permitirEliminarNodo && tipo !== 'inicio';
    const canAddChild = permitirAgregarHijo && tipo !== 'fin';
    const canEditAct = !!editarActividad;

    return (
        <div style={s.panel}>

            {/* Header */}
            <div style={s.header}>
                <span style={s.title}>Propiedades del nodo</span>
                <TypeBadge tipo={tipo} />
            </div>

            <div style={s.divider} />

            {/* Fields */}
            <div style={s.fields}>
                {/* Node ID (read-only info) */}
                <div style={s.fieldGroup}>
                    <label style={s.label}>ID</label>
                    <div style={s.readOnly}>{selectedNode?.id}</div>
                </div>

                {/* Tipo de nodo */}
                {editarTipoNodo && (
                    <div style={s.fieldGroup}>
                        <label style={s.label}>Tipo de nodo</label>
                        <select
                            style={s.input}
                            value={tipoEdit}
                            onChange={e => setTipoEdit(e.target.value as NodeTipo)}
                        >
                            <option value="inicio">Inicio</option>
                            <option value="actividad">Actividad</option>
                            <option value="fin">Fin</option>
                        </select>
                    </div>
                )}

                {/* Categoría de actividad – only for actividad */}
                {tipo === 'actividad' && (
                    <div style={s.fieldGroup}>
                        <label style={s.label}>Categoría de actividad</label>
                        <select
                            style={s.input}
                            value={categoriaId}
                            onChange={e => setCategoriaId(e.target.value)}
                            disabled={!canEditAct}
                        >
                            <option value="">— Sin categoría —</option>
                            {categorias.map(c => (
                                <option key={c.id} value={String(c.id)}>
                                    {c.descripcion ?? c.nombre ?? `#${c.id}`}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Habilitado – only for actividad */}
                {mostrarConfigActividad && tipo === 'actividad' && (
                    <label style={s.checkRow}>
                        <input
                            type="checkbox"
                            style={s.checkInput}
                            checked={habilitado}
                            onChange={e => setHabilitado(e.target.checked)}
                        />
                        <span style={s.label}>Habilitado</span>
                    </label>
                )}

                {mostrarConfigActividad && tipo === 'actividad' && (
                    <label style={s.checkRow}>
                        <input
                            type="checkbox"
                            style={s.checkInput}
                            checked={requiereAutorizacion}
                            onChange={e => setRequiereAutorizacion(e.target.checked)}
                        />
                        {requiereAutorizacion && <ShieldIcon show={true} />}

                        <span style={s.label}>Requiere Autorización</span>
                    </label>
                )}

                {/* Usuario – autocomplete, only for actividad */}
                {permitirSeleccionarUsuario && tipo === 'actividad' && (
                    <div style={s.fieldGroup}>
                        <label style={s.label}>Usuario</label>
                        <UsuariosFilter
                            value={usuario}
                            onChange={setUsuario}
                            placeholder="Buscar usuario…"
                        />
                    </div>
                )}

                {/* Autoriza – autocomplete, only for actividad */}
                {permitirSeleccionarUsuario && tipo === 'actividad' && requiereAutorizacion && (
                    <div style={s.fieldGroup}>
                        <label style={s.label}>Autoriza</label>
                        <UsuariosFilter
                            value={autoriza}
                            onChange={setAutoriza}
                            placeholder="Buscar usuario que autoriza…"
                        />
                    </div>
                )}
            </div>

            {/* Apply button */}
            <button style={s.btnApply} onClick={handleApply}>
                <FaCheck style={{ marginRight: 6 }} />
                Aplicar cambios
            </button>

            <div style={s.divider} />

            {/* Action buttons */}
            <div style={s.actions}>
                {/* {canAddChild && (
                    <button
                        style={s.btnAction}
                        onClick={() => addNodeFromSelected(selectedNode.id)}
                    >
                        <FaPlus style={{ marginRight: 6 }} />
                        Agregar actividad
                    </button>
                )} */}

                {/* {canDelete && (
                    <button style={s.btnDanger} onClick={() => removeNode(selectedNode)}>
                        <FaTrash style={{ marginRight: 6 }} />
                        Eliminar nodo
                    </button>
                )} */}
            </div>

            {children}
        </div>
    );
}

// ── Badge ────────────────────────────────────────────────────────────────────

function TypeBadge({ tipo }: { tipo: NodeTipo }) {
    const colors: Record<NodeTipo, { bg: string; text: string }> = {
        inicio: { bg: '#dcfce7', text: '#166534' },
        actividad: { bg: '#eff6ff', text: '#1e40af' },
        fin: { bg: '#fef2f2', text: '#991b1b' },
    };
    const labels: Record<NodeTipo, string> = {
        inicio: 'Inicio', actividad: 'Actividad', fin: 'Fin',
    };
    const c = colors[tipo] ?? { bg: '#f1f5f9', text: '#475569' };
    return (
        <span style={{
            padding: '2px 10px', borderRadius: 20, fontSize: 11,
            fontWeight: 700, background: c.bg, color: c.text,
            letterSpacing: '0.04em',
        }}>
            {labels[tipo] ?? tipo}
        </span>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
    panel: {
        display: 'flex', flexDirection: 'column', gap: 12,
        padding: '16px 0',
    },
    header: {
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 8,
    },
    title: { fontSize: 13, fontWeight: 700, color: 'var(--crm-text-primary, #0f172a)' },
    divider: { borderTop: '1px solid var(--crm-border, #e2e8f0)', margin: '4px 0' },

    fields: { display: 'flex', flexDirection: 'column', gap: 12 },
    fieldGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
    label: { fontSize: 11, fontWeight: 600, color: 'var(--crm-text-secondary, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em' } as React.CSSProperties,
    readOnly: { fontSize: 13, color: 'var(--crm-text-primary, #1e293b)', padding: '6px 0', borderBottom: '1px solid var(--crm-border, #e2e8f0)' },
    input: {
        padding: '8px 10px', border: '1px solid var(--crm-border)', borderRadius: 7,
        fontSize: 13, color: 'var(--crm-text-primary)', outline: 'none',
        width: '100%', boxSizing: 'border-box' as const, background: 'var(--crm-input-bg)',
    },
    checkRow: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 0' } as React.CSSProperties,
    checkInput: { width: 15, height: 15, accentColor: '#6366f1', cursor: 'pointer', flexShrink: 0 } as React.CSSProperties,

    btnApply: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '8px 14px', background: '#6366f1', color: '#fff',
        border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 600,
        cursor: 'pointer', width: '100%',
    },
    actions: { display: 'flex', flexDirection: 'column', gap: 8 },
    btnAction: {
        display: 'flex', alignItems: 'center',
        padding: '7px 12px', background: '#f8fafc', color: '#334155',
        border: '1px solid #e2e8f0', borderRadius: 7,
        fontSize: 12, fontWeight: 600, cursor: 'pointer', width: '100%',
    },
    btnDanger: {
        display: 'flex', alignItems: 'center',
        padding: '7px 12px', background: '#fef2f2', color: '#b91c1c',
        border: '1px solid #fecaca', borderRadius: 7,
        fontSize: 12, fontWeight: 600, cursor: 'pointer', width: '100%',
    },
};
